/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import UploadButton from "../../components/UploadButton";
import { getTrainer, updateTrainer } from "../../services/trainerService";
import SuccessMessage from "../../components/SuccessMessage";
import ErrorMessage from "../../components/ErrorMessage";
import Header from "../../components/Header";
import BackButton from "../../components/BackButton";

interface TrainerFormValues {
  name: string;
  qualification: string;
  description: string;
  experience: string;
  photoUrl: string | null;
  instagramUrl: string;
  tiktokUrl: string;
}

const TrainerValidationSchema = Yup.object().shape({
  name: Yup.string()
    .required("Name is required")
    .matches(/^(?!\s*$).+/, "Name cannot be empty or just spaces"),
  qualification: Yup.string()
    .required("Qualification is required")
    .matches(/^(?!\s*$).+/, "Qualification cannot be empty or just spaces"),
  experience: Yup.string()
    .required("Experience is required")
    .matches(/^(?!\s*$).+/, "Experience cannot be empty or just spaces"),
});

const EditTrainer: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [initialValues, setInitialValues] = useState<TrainerFormValues>({
    name: "",
    qualification: "",
    experience: "",
    description: "",
    photoUrl: null,
    instagramUrl: "",
    tiktokUrl: "",
  });
  const [file, setFile] = useState<File | null>(null);

  useEffect(() => {
    if (id) {
      getTrainer(Number(id)).then((trainer) => {
        setInitialValues({
          name: trainer.name,
          qualification: trainer.qualification,
          experience: trainer.experience,
          description: trainer.description,
          photoUrl: trainer.photoUrl || null,
          instagramUrl: trainer.instagramUrl || "",
          tiktokUrl: trainer.tiktokUrl || "",
        });
      });
    }
  }, [id]);

  const handleSubmit = async (values: typeof initialValues) => {
    const formData = new FormData();
    formData.append("name", values.name);
    formData.append("experience", values.experience);
    formData.append("qualification", values.qualification);
    formData.append("description", values.description);
    if (values.instagramUrl) formData.append("instagramUrl", values.instagramUrl);
    if (values.tiktokUrl) formData.append("tiktokUrl", values.tiktokUrl);
    if (file) formData.append("photo", file);
    console.log("df");
    try {
      await updateTrainer(Number(id), formData);
      navigate("/trainers");
    } catch (err) {
      console.log(err);
      console.error(err);
      setError(err instanceof Error ? err.message : "Failed to save trainer");
    }
  };

  return (
    <div className="p-6 bg-gray-100 min-h-screen flex flex-col">
      <Header title={"Edit Trainer"} />
      <div className="w-full max-w-lg bg-white shadow-md rounded-md p-6">
        {error && (
          <ErrorMessage message={error} onClose={() => setError(null)} />
        )}
        {successMessage && (
          <SuccessMessage
            message={successMessage}
            onClose={() => setSuccessMessage(null)}
          />
        )}

        <div className="flex items-center justify-between mt-4 border-b pb-4">
          <BackButton onClick={() => navigate("/trainers")} />
        </div>

        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={TrainerValidationSchema}
          onSubmit={handleSubmit}
        >
          {({ errors, touched, setFieldValue, values, dirty }) => (
            <Form className="space-y-4">
              <div>
                <label className="block text-gray-700 font-medium mb-2">
                  Name
                </label>
                <Field
                  name="name"
                  type="text"
                  placeholder="Trainer Name"
                  className="w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring focus:ring-blue-500"
                />
                {errors.name && touched.name && (
                  <div className="text-red-500 text-xs mt-1">{errors.name}</div>
                )}
              </div>
              <div>
                <label className="block text-gray-700 font-medium mb-2">
                  Qualification
                </label>
                <Field
                  name="qualification"
                  type="text"
                  placeholder="Trainer Qualification"
                  className="w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring focus:ring-blue-500"
                />
                {errors.qualification && touched.qualification && (
                  <div className="text-red-500 text-xs mt-1">
                    {errors.qualification}
                  </div>
                )}
              </div>
              <div>
                <label className="block text-gray-700 font-medium mb-2">
                  Experience
                </label>
                <Field
                  name="experience"
                  type="text"
                  placeholder="Experience"
                  className="w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring focus:ring-blue-500"
                />
                {errors.experience && touched.experience && (
                  <div className="text-red-500 text-xs mt-1">
                    {errors.experience}
                  </div>
                )}
              </div>
              <div>
                <label>Description</label>
                <Field
                  name="description"
                  type="text"
                  placeholder="Description"
                  className="w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring focus:ring-blue-500"
                />
                {errors.description && touched.description && (
                  <div className="text-red-500">{errors.description}</div>
                )}
              </div>

              <div>
                <label className="block text-gray-700 font-medium mb-2">
                  Instagram URL
                </label>
                <Field
                  name="instagramUrl"
                  type="text"
                  placeholder="https://instagram.com/username"
                  className="w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-gray-700 font-medium mb-2">
                  TikTok URL
                </label>
                <Field
                  name="tiktokUrl"
                  type="text"
                  placeholder="https://tiktok.com/@username"
                  className="w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-gray-700 font-medium mb-2">
                  Photo
                </label>
                {initialValues.photoUrl && (
                  <div className="flex flex-col items-center">
                    <img
                      src={initialValues.photoUrl}
                      alt=""
                      className="w-24 h-24 rounded-full mb-2"
                    />
                  </div>
                )}
                <UploadButton
                  label="Upload Photo"
                  onUpload={async (f: File | null) => {
                    setFile(f);

                    const previewUrl = f ? URL.createObjectURL(f) : null;
                    await setFieldValue("photoUrl", previewUrl);

                    return previewUrl;
                  }}
                  onRemove={() => {
                    setFile(null);
                    return Promise.resolve(null);
                  }}
                  accept="image/*"
                  index={0}
                />
              </div>

              <button
                type="submit"
                disabled={!dirty}
                className={`w-full py-2 text-white font-semibold rounded-md transition ${
                  dirty
                    ? "bg-yellow-600 hover:bg-yellow-700"
                    : "bg-gray-400 cursor-not-allowed"
                }`}
              >
                Update Trainer
              </button>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default EditTrainer;
